import { FC, useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { FormType, SubmissionMode } from '@weave/schema-gen-ts/dist/schemas/forms-digital/weave_digital_forms.pb';
import { useTranslation } from '@frontend/i18n';
import { useLatestRef } from '@frontend/state-ref-helpers';
import { ContentLoader, useAlert } from '@frontend/design-system';
import { buildAutofillValues } from '../../../../autofill';
import { DraftAutosaveBridge } from '../../../../draft/draft-autosave-bridge';
import { restoreDraftMedia } from '../../../../draft/draft-media-restore';
import { restoreDraftPaymentMethods } from '../../../../draft/draft-payment-restore';
import { parseDraftCountryCode, parseDraftResponse } from '../../../../draft/draft-serializer';
import { trackDraftEvent } from '../../../../draft/draft-telemetry';
import { AutosaveStatus, RestoreNotice } from '../../../../draft/draft.types';
import { useDraftAutosave } from '../../../../draft/use-draft-autosave';
import { hasDatePassed } from '../../../../helpers/date-helpers';
import { countryCodeToPostalCodeLocale } from '../../../../helpers/locale-helpers';
import {
  useDraftApi,
  useDraftAvailability,
  useFetchStateList,
  useGetFormTemplate,
  useGetLocationDetails,
} from '../../../../hooks/api-hooks';
import { useLeavePageConfirm } from '../../../../hooks/use-leave-page-confirm';
import { useConfigShallowStore } from '../../../../stores';
import { FillingOptionScreen } from '../../../kiosk/components';
import ContentContainer from '../../../layout/content-container.component';
import Header from '../../../layout/header.component';
import { AutosaveRecoveryNotice } from '../../components/draft/autosave-recovery-notice';
import { AutosaveStatusIndicator } from '../../components/draft/autosave-status-indicator';
import { ResumeDraftScreen } from '../../components/draft/resume-draft-screen';
import { DynamicFormContainer } from '../../components/dynamic-form-container/dynamic-form-container';
import {
  getPaymentContext,
  usePaymentMethodsFetcher,
} from '../../components/dynamic-form-container/fields/payment-on-file-field/use-payment-api';
import { convertSchemaFormsToDynamicFormStoreState } from '../../components/dynamic-form-container/helpers';
import { useSubmitDynamicForm } from '../../components/dynamic-form-container/hooks/use-submit-dynamic-form';
import { MediaAttachment } from '../../components/dynamic-form-container/media-upload/types';
import { DynamicFormLiveState } from '../../components/dynamic-form-container/submit-form.helpers';
import { OTPScreen } from '../../components/form-components/otp-screen';
import { VerifySecretCode } from '../../components/form-components/otp-screen/verify-secret-code';
import { SubmissionConfirmation } from '../../components/form-components/submission-confirmation';
import { FormNotFound } from '../../components/form-not-found';
import { PatronFormWelcomeScreen } from '../../components/patron-form-welcome-screen';
import { FillingExperienceProps } from '../../filling-experience.type';
import { useDuplicateSubmissionGuard } from '../../hooks/use-duplicate-submission-guard';
import { applyLocaleToSchemaForms, useLocaleSchemaForms } from '../../hooks/use-locale-schema-forms';
import { FillingExperienceWrapper } from '../../provider';

type Steps =
  | 'magic-link-verify'
  | 'welcome'
  | 'checking-draft'
  | 'resume-draft'
  | 'filling-options'
  | 'otp-verification'
  | 'dynamic-form'
  | 'submitted';

/** Statuses where leaving the page would lose edits that aren't saved yet. */
const UNSAVED_STATUSES: AutosaveStatus[] = ['dirty', 'saving', 'retrying', 'paused', 'offline', 'unavailable'];

type SolicitedFormProps = FillingExperienceProps & {
  secretCode: string;
  /** The link was generated for a dependent (`for_dependent=true`). */
  isDependent?: boolean;
};

export const SolicitedForm: FC<SolicitedFormProps> = (props) => {
  const { locationId, documentId, secretCode, isDependent, isPreviewMode = false } = props;
  const { t } = useTranslation('digital-forms-experience');
  const alerts = useAlert();

  const [currentStep, setCurrentStep] = useState<Steps>('welcome');
  const [initialValues, setInitialValues] = useState<Record<string, unknown>>({});
  const [initialActiveSectionIndex, setInitialActiveSectionIndex] = useState(0);
  const [initialAttachments, setInitialAttachments] = useState<Record<string, MediaAttachment>>();
  const [restoreNotice, setRestoreNotice] = useState<RestoreNotice>(null);
  /** Set once an existing draft has been restored or discarded, so new writes can't overwrite it unseen. */
  const [isDraftResolved, setIsDraftResolved] = useState(false);

  const { person, setPerson, postalCodeLocale, restorePostalCodeLocale, clearRestoredPostalCodeLocale } =
    useConfigShallowStore(
      'person',
      'setPerson',
      'postalCodeLocale',
      'restorePostalCodeLocale',
      'clearRestoredPostalCodeLocale'
    );
  const fetchStateList = useFetchStateList();

  useEffect(() => clearRestoredPostalCodeLocale, [clearRestoredPostalCodeLocale]);

  const { locationDetails } = useGetLocationDetails(locationId);
  const {
    formTemplate,
    isLoading: isFormLoading,
    isError,
    isNotRetryableError,
    isEmptyForm,
    refetch,
  } = useGetFormTemplate({
    companyId: locationId,
    qwe: documentId,
    includeOptions: true,
  });

  const schemaForms = useLocaleSchemaForms(formTemplate?.data ?? []);
  const isSubmitted = !!formTemplate?.isSubmitted;
  const hasFormExpired = hasDatePassed(formTemplate?.expiryDate);

  const isDraftFeatureEnabled = !isPreviewMode && !!formTemplate && !isSubmitted && !hasFormExpired;
  const draftAvailability = useDraftAvailability({
    companyId: locationId,
    submissionId: documentId,
    enabled: isDraftFeatureEnabled,
  });
  const { verifyOwner, fetchDraft, deleteDraft } = useDraftApi({ companyId: locationId, submissionId: documentId });

  const canWriteDraft =
    isDraftFeatureEnabled &&
    (draftAvailability.status === 'eligible-no-draft' ||
      (draftAvailability.status === 'eligible-has-draft' && isDraftResolved));
  const isDraftAvailabilityUnknown =
    draftAvailability.status === 'eligibility-unknown' || draftAvailability.status === 'existence-unknown';
  const hasUnresolvedDraft = draftAvailability.status === 'eligible-has-draft' && !isDraftResolved;

  const {
    coordinator,
    status: autosaveStatus,
    adoptDraft,
    resetLifecycle,
  } = useDraftAutosave({
    companyId: locationId,
    submissionId: documentId,
    enabled: currentStep === 'dynamic-form' && canWriteDraft,
  });

  const currentStepRef = useLatestRef(currentStep);

  // Config store is global; clear on exit so the next solicited form doesn't show this patient's name.
  useEffect(() => {
    // Read the step via ref so step changes don't re-run this effect (which would reset the person, e.g. the
    // autofilled one). Skip once submitted so a template refetch can't set the person back.
    if (!formTemplate?.personDetails || currentStepRef.current === 'submitted') return;
    setPerson(formTemplate.personDetails);
    return () => setPerson(undefined);
  }, [formTemplate?.personDetails, setPerson, currentStepRef]);

  // Draft restore runs before the form (and its filling-experience context) mounts, so build the context here.
  const paymentContext = useMemo(
    () => getPaymentContext(person?.id, locationId, documentId),
    [documentId, locationId, person?.id]
  );
  const fetchPaymentMethods = usePaymentMethodsFetcher(paymentContext);

  const { submitForm, isSubmitting, submissionResponse } = useSubmitDynamicForm({
    schemaForms,
    locationId,
    type: schemaForms.length > 1 ? FormType.TYPE_PACKET : FormType.TYPE_FORM,
    mode: SubmissionMode.SOLICITED_DIRECT,
    submissionId: documentId,
  });

  // Multi-tab guard: another tab may have already submitted this form while this one was open.
  useDuplicateSubmissionGuard({
    enabled: currentStep !== 'submitted',
    isSubmitted,
    refetch,
  });

  const autosaveStatusRef = useLatestRef(autosaveStatus);
  const coordinatorRef = useLatestRef(coordinator);

  const { confirmationModal: leavePageModal } = useLeavePageConfirm({
    shouldBlockFn: () =>
      isDraftFeatureEnabled &&
      currentStepRef.current === 'dynamic-form' &&
      UNSAVED_STATUSES.includes(autosaveStatusRef.current) &&
      (autosaveStatusRef.current !== 'unavailable' || !!coordinatorRef.current?.hasUnsavedChanges()),
    title: t('Leave this page?'),
    message: t("Some of your recent changes aren't saved yet."),
    confirmLabel: t('Leave without saving recent changes'),
    cancelLabel: t('Stay and save'),
  });

  // Welcome was accepted before the draft check finished: route once it resolves.
  const draftAvailabilityStatus = draftAvailability.status;
  const stepAfterDraftCheck = secretCode ? 'magic-link-verify' : 'filling-options';
  useEffect(() => {
    if (currentStep !== 'checking-draft' || (isDraftFeatureEnabled && draftAvailabilityStatus === 'loading')) return;
    if (isDraftFeatureEnabled && draftAvailabilityStatus === 'eligible-has-draft') {
      setCurrentStep('resume-draft');
    } else {
      setCurrentStep(stepAfterDraftCheck);
    }
  }, [currentStep, draftAvailabilityStatus, isDraftFeatureEnabled, stepAfterDraftCheck]);

  // Move focus to the form heading after a restore so the change of context is announced (spec §5.3).
  useEffect(() => {
    if (currentStep !== 'dynamic-form' || !restoreNotice) return;
    const frame = requestAnimationFrame(() => {
      document.querySelector<HTMLElement>('[data-dynamic-form-heading]')?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [currentStep, restoreNotice]);

  const moveTo = (step: Steps) => () => setCurrentStep(step);
  const handleWelcomeAccept = () => {
    setCurrentStep(isPreviewMode ? stepAfterDraftCheck : 'checking-draft');
  };

  if (isFormLoading) {
    return (
      <div
        css={css`
          height: 100vh;
        `}
      >
        <ContentLoader show message='Loading form...' />
      </div>
    );
  }

  if (!formTemplate || isError) {
    if (isNotRetryableError) return <FormNotFound variant='not-found' locationId={locationId} />;
    return <FormNotFound variant='error' onRetry={refetch} locationId={locationId} />;
  }

  const officeName = locationDetails?.location_nickname || locationDetails?.name || '';
  const logoUrl = formTemplate.logoUrl ?? '';

  if (hasFormExpired) {
    return <FormNotFound variant='expired' locationId={locationId} logoUrl={logoUrl} />;
  }

  if (isSubmitted && currentStep !== 'submitted') {
    return <FormNotFound variant='already-submitted' locationId={locationId} logoUrl={logoUrl} />;
  }

  if (isEmptyForm) {
    return <FormNotFound variant='empty-form' onRetry={refetch} locationId={locationId} logoUrl={logoUrl} />;
  }

  const handleAutofillSuccess = (data: Parameters<typeof buildAutofillValues>[0]) => {
    setPerson(data.personDetails);
    setInitialValues(buildAutofillValues(data));
    setCurrentStep('dynamic-form');
  };

  const handleRestoreDraft = async (draft: Parameters<typeof parseDraftResponse>[0]) => {
    // Parse against the saved country's states, and apply it before mounting so `resetOnSchemaChange` can't clear them.
    const savedCountryCode = parseDraftCountryCode(draft);
    const locale = savedCountryCode ? countryCodeToPostalCodeLocale(savedCountryCode) : postalCodeLocale;
    const stateList = await fetchStateList(locale);
    const draftFormFieldsMap = convertSchemaFormsToDynamicFormStoreState(
      applyLocaleToSchemaForms(formTemplate.data ?? [], stateList),
      {}
    ).formFieldsMap;

    const parsed = parseDraftResponse(draft, { formFieldsMap: draftFormFieldsMap, submissionId: documentId });
    if (!parsed.ok) {
      trackDraftEvent('Draft restore rejected', { reason: parsed.reason }, 'warning');
      return false;
    }

    const media = await restoreDraftMedia({ answers: parsed.snapshot.answers, formFieldsMap: draftFormFieldsMap });
    const payments = await restoreDraftPaymentMethods({
      answers: media.answers,
      formFieldsMap: draftFormFieldsMap,
      fetchPaymentMethods,
    });
    const isPartial = parsed.droppedKeys.length > 0 || media.failedKeys.length > 0 || payments.failedKeys.length > 0;
    trackDraftEvent('Draft restored', {
      partial: isPartial,
      droppedCount: parsed.droppedKeys.length,
      failedMediaCount: media.failedKeys.length,
      failedPaymentCount: payments.failedKeys.length,
    });

    if (parsed.snapshot.countryCode) {
      restorePostalCodeLocale(countryCodeToPostalCodeLocale(parsed.snapshot.countryCode));
    }
    setInitialValues(payments.answers);
    setInitialActiveSectionIndex(parsed.snapshot.activeSectionIndex);
    setInitialAttachments(media.attachments);
    adoptDraft({ draftId: parsed.metadata.draftId, version: parsed.metadata.version });
    setIsDraftResolved(true);
    setRestoreNotice(isPartial ? 'partial' : 'restored');
    setCurrentStep('dynamic-form');
    return true;
  };

  const handleDiscardDraft = async () => {
    const result = await deleteDraft();
    if (result.type === 'error') return false;

    resetLifecycle();
    setIsDraftResolved(true);
    setCurrentStep(secretCode ? 'magic-link-verify' : 'filling-options');
    return true;
  };

  const handleReviewSubmit = async (liveState: DynamicFormLiveState) => {
    // Submission uses the live form state; autosave must not race it (spec §8.2.7).
    coordinator?.suspend();
    try {
      await submitForm(liveState);
      coordinator?.dispose();
      setPerson(undefined);
      setCurrentStep('submitted');
    } catch {
      coordinator?.resume();
      alerts.error(t('Something went wrong submitting your form. Please try again.'));
    }
  };

  const showAutosaveUi = isDraftFeatureEnabled && (!!coordinator || isDraftAvailabilityUnknown || hasUnresolvedDraft);
  const indicatorStatus: AutosaveStatus = isSubmitting ? 'submitting' : coordinator ? autosaveStatus : 'unavailable';

  return (
    <FillingExperienceWrapper {...props} experienceType='solicited'>
      <Header
        showCountrySelector={currentStep !== 'submitted'}
        logoUrl={logoUrl}
        officeName={officeName}
        patient={
          person?.firstName
            ? { firstName: person.firstName, lastName: person.lastName, isDependent: !!isDependent }
            : undefined
        }
      />
      <ContentContainer>
        {currentStep === 'welcome' && (
          <PatronFormWelcomeScreen
            locationId={locationId}
            patientFirstName={person?.firstName}
            onAccept={handleWelcomeAccept}
          />
        )}
        {currentStep === 'checking-draft' && <ContentLoader show message={t('Checking for saved progress…')} />}
        {currentStep === 'resume-draft' && (
          <ResumeDraftScreen
            verifyOwner={verifyOwner}
            fetchDraft={fetchDraft}
            onRestore={handleRestoreDraft}
            onDiscard={handleDiscardDraft}
          />
        )}
        {currentStep === 'filling-options' && (
          <FillingOptionScreen
            locationId={locationId}
            officeName={officeName}
            onAutoFill={moveTo('otp-verification')}
            onManualFill={moveTo('dynamic-form')}
          />
        )}
        {currentStep === 'magic-link-verify' && (
          <VerifySecretCode
            secretCode={secretCode}
            qwe={documentId}
            companyId={locationId}
            onSuccess={handleAutofillSuccess}
            onFillManually={moveTo('dynamic-form')}
          />
        )}
        {currentStep === 'otp-verification' && (
          <OTPScreen
            qwe={documentId}
            locationId={locationId}
            officeName={officeName}
            onSuccess={handleAutofillSuccess}
            onFillManually={moveTo('dynamic-form')}
          />
        )}
        {currentStep === 'dynamic-form' && (
          <DynamicFormContainer
            schemaForms={schemaForms}
            initialValues={initialValues}
            initialActiveSectionIndex={initialActiveSectionIndex}
            initialAttachments={initialAttachments}
            isSubmitting={isSubmitting}
            onReviewSubmit={handleReviewSubmit}
            officeNote={formTemplate.officeNote}
            renderAutosaveIndicator={showAutosaveUi && <AutosaveStatusIndicator status={indicatorStatus} />}
          >
            {isDraftFeatureEnabled && coordinator && <DraftAutosaveBridge coordinator={coordinator} />}
            {showAutosaveUi && (
              <AutosaveRecoveryNotice
                status={isSubmitting ? 'submitting' : autosaveStatus}
                restoreNotice={restoreNotice}
                availabilityUnknown={isDraftAvailabilityUnknown}
                unresolvedDraft={hasUnresolvedDraft}
                isRetryingAvailability={draftAvailability.isRefetching}
                onRetrySave={() => coordinator?.retryNow()}
                onRetryAvailability={draftAvailability.refetch}
                onResumeDraft={moveTo('resume-draft')}
                onDismissRestoreNotice={() => setRestoreNotice(null)}
              />
            )}
          </DynamicFormContainer>
        )}
        {currentStep === 'submitted' && <SubmissionConfirmation submissionId={submissionResponse?.submissionId} />}
      </ContentContainer>
      {leavePageModal}
    </FillingExperienceWrapper>
  );
};
